import React, { createContext, useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Create the UserContext
export const UserContext = createContext({
  user: null,
  firebaseUser: null,
  isLoading: true,
  error: null,
  savedItems: [],
  dismissedItems: [],
  saveItem: () => {},
  dismissItem: () => {},
  updateUser: () => {},
  updateUserPreferences: () => {},
  updateSignalWeight: () => {},
  addInfluence: () => {},
  removeInfluence: () => {},
  updateScoreThreshold: () => {},
  isSaved: () => false,
  isDismissed: () => false,
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedItems, setSavedItems] = useState([]);
  const [dismissedItems, setDismissedItems] = useState([]);

  // Load Digital Card data from Firebase (safely)
  const loadFirebaseUserData = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const firebaseData = userDocSnap.data();
        
        // Only return Digital Card specific data to avoid conflicts
        return {
          title: firebaseData.title,
          bio: firebaseData.bio,
          cardImage: firebaseData.cardImage,
          visibilityMode: firebaseData.visibilityMode,
          forwardingEmail: firebaseData.forwardingEmail,
          curationSignature: firebaseData.curationSignature,
          settings: {
            ...firebaseData.settings,
            // Merge with any existing settings, Firebase takes precedence for Digital Card settings
          },
          // Digital Card timestamps
          digitalCardCreatedAt: firebaseData.createdAt,
          digitalCardUpdatedAt: firebaseData.updatedAt
        };
      }
      
      return null;
    } catch (error) {
      console.warn('⚠️ Could not load Firebase user data:', error.message);
      return null; // Fail gracefully, continue with localStorage data
    }
  };

  // Load user data from localStorage with Firebase Digital Card data integration
  const loadUserData = async (currentUser) => {
    try {
      if (currentUser) {
        // User is logged in, set Firebase user immediately
        setFirebaseUser(currentUser);
        
        // Check Firebase auth claims for admin status
        let isAdmin = false;
        let userClaims = {};
        
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          userClaims = idTokenResult.claims;
          isAdmin = userClaims.admin === true || currentUser.email === 'matt.c.goff@gmail.com';
        } catch (claimsError) {
          console.warn('⚠️ Could not get user claims:', claimsError);
          isAdmin = currentUser.email === 'matt.c.goff@gmail.com';
        }
        
        // Load their data
        const userId = currentUser.uid;
        
        // Load user preferences from localStorage
        const storedUser = localStorage.getItem(`artifact_user_${userId}`);
        
        // Load Digital Card data from Firebase
        const firebaseDigitalCardData = await loadFirebaseUserData(userId);
        
        let finalUser;
        
        if (storedUser) {
          // Parse stored user data but ensure Firebase user properties
          const userData = JSON.parse(storedUser);
          finalUser = {
            ...userData,
            uid: currentUser.uid,
            email: currentUser.email,
            emailVerified: currentUser.emailVerified,
            displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
            // Role fields based on Firebase claims and email
            userRole: isAdmin ? 'admin' : (userData.userRole || 'guest'),
            approvedForUploads: isAdmin ? true : (userData.approvedForUploads || false),
            isGuest: false, // Never a guest if authenticated
            // Store Firebase claims for reference
            firebaseClaims: userClaims,
            
            // CRITICAL FIX: Merge Firebase Digital Card data with localStorage data
            // Firebase data takes precedence for Digital Card fields
            ...(firebaseDigitalCardData || {}),
            
            // Ensure curationSignature is properly merged
            curationSignature: {
              // Start with localStorage curationSignature
              preferredTags: userData.curationSignature?.preferredTags || [],
              avoidedTags: userData.curationSignature?.avoidedTags || [],
              influences: userData.curationSignature?.influences || [],
              materialBias: userData.curationSignature?.materialBias || [],
              colorPalette: userData.curationSignature?.colorPalette || [],
              styleBias: userData.curationSignature?.styleBias || [],
              scoreThreshold: userData.curationSignature?.scoreThreshold || 0.6,
              signalWeights: userData.curationSignature?.signalWeights || {},
              // Override with Firebase Digital Card data if available
              ...(firebaseDigitalCardData?.curationSignature || {})
            },
            
            // Ensure settings are properly merged
            settings: {
              // Start with localStorage settings
              filterMode: userData.settings?.filterMode || 'global',
              public: userData.settings?.public || false,
              // Override with Firebase Digital Card settings if available
              ...(firebaseDigitalCardData?.settings || {})
            }
          };
        } else {
          // Initialize with basic user data from auth
          finalUser = {
            uid: userId,
            email: currentUser.email,
            emailVerified: currentUser.emailVerified,
            displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
            bio: 'Design enthusiast',
            // Role fields for new users based on Firebase claims
            userRole: isAdmin ? 'admin' : 'guest',
            approvedForUploads: isAdmin ? true : false,
            isGuest: false, // Authenticated users are never guests
            firebaseClaims: userClaims,
            
            // CRITICAL FIX: Include Firebase Digital Card data for new users
            ...(firebaseDigitalCardData || {}),
            
            curationSignature: {
              preferredTags: [],
              avoidedTags: [],
              influences: [],
              materialBias: [],
              colorPalette: [],
              styleBias: [],
              scoreThreshold: 0.6,
              signalWeights: {},
              // Override with Firebase data if available
              ...(firebaseDigitalCardData?.curationSignature || {})
            },
            history: {
              likedItemIds: [],
              dismissedItemIds: [],
              lastUpdated: Date.now()
            },
            settings: {
              filterMode: 'global',
              public: false,
              // Override with Firebase data if available
              ...(firebaseDigitalCardData?.settings || {})
            }
          };
          
          localStorage.setItem(`artifact_user_${userId}`, JSON.stringify(finalUser));
        }
        
        setUser(finalUser);
        
        // Load saved items
        const savedItemsData = localStorage.getItem(`artifact_saved_${userId}`);
        if (savedItemsData) {
          setSavedItems(JSON.parse(savedItemsData));
        }
        
        // Load dismissed items
        const dismissedItemsData = localStorage.getItem(`artifact_dismissed_${userId}`);
        if (dismissedItemsData) {
          setDismissedItems(JSON.parse(dismissedItemsData));
        }
      } else {
        // User is not logged in
        setFirebaseUser(null);
        
        // Generate or retrieve guest ID
        const guestId = localStorage.getItem('artifact_guest_id') || 
                       `guest_${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem('artifact_guest_id', guestId);
        
        // Load guest user data
        const storedGuestUser = localStorage.getItem(`artifact_user_${guestId}`);
        
        let finalGuestUser;
        
        if (storedGuestUser) {
          finalGuestUser = JSON.parse(storedGuestUser);
        } else {
          // Initialize guest user
          finalGuestUser = {
            uid: guestId,
            displayName: 'Guest User',
            bio: 'Design explorer',
            isGuest: true,
            userRole: 'guest',
            approvedForUploads: false,
            emailVerified: false, // Guests don't have verified emails
            curationSignature: {
              preferredTags: [],
              avoidedTags: [],
              influences: [],
              materialBias: [],
              colorPalette: [],
              styleBias: [],
              scoreThreshold: 0.6,
              signalWeights: {},
            },
            history: {
              likedItemIds: [],
              dismissedItemIds: [],
              lastUpdated: Date.now()
            },
            settings: {
              filterMode: 'global',
              public: false,
            }
          };
          
          localStorage.setItem(`artifact_user_${guestId}`, JSON.stringify(finalGuestUser));
        }
        
        setUser(finalGuestUser);
        
        // Load guest saved/dismissed items
        const guestSavedItems = localStorage.getItem(`artifact_saved_${guestId}`);
        if (guestSavedItems) {
          setSavedItems(JSON.parse(guestSavedItems));
        }
        
        const guestDismissedItems = localStorage.getItem(`artifact_dismissed_${guestId}`);
        if (guestDismissedItems) {
          setDismissedItems(JSON.parse(guestDismissedItems));
        }
      }
    } catch (err) {
      console.error('❌ Error loading user data:', err);
      setError(err.message);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };
      
  // Listen for auth state changes
useEffect(() => {
  // Set a timeout to prevent infinite loading
  const timeoutId = setTimeout(() => {
    console.log('Firebase auth timeout - continuing as guest');
    setIsLoading(false);
  }, 500); // .5 second timeout

  const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
    clearTimeout(timeoutId); // Clear timeout since auth worked
    setIsLoading(true);
    await loadUserData(authUser);
  }, (error) => {
    clearTimeout(timeoutId); // Clear timeout on error
    console.error('❌ UserContext: Auth error:', error);
    setError(error.message);
    setIsLoading(false);
  });
  
  return () => {
    clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  // Helper function to get user ID (either authenticated or guest)
  const getUserId = () => {
    if (!user) return null;
    return user.uid;
  };
  
  // Save user data to localStorage
  const persistUserData = () => {
    const userId = getUserId();
    if (!userId || !user) return;
    
    localStorage.setItem(`artifact_user_${userId}`, JSON.stringify(user));
  };
  
  // Update the entire user object
  const updateUser = (userData) => {
    setUser(userData);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Update user preferences
  const updateUserPreferences = (preferences) => {
    if (!user) return;
    
    const updatedUser = {
      ...user,
      curationSignature: {
        ...user.curationSignature,
        ...preferences
      },
      history: {
        ...user.history,
        lastUpdated: Date.now()
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Update a single signal weight
  const updateSignalWeight = (signal, weight) => {
    if (!user) return;
    
    const weightValue = parseInt(weight, 10);
    
    if (isNaN(weightValue) || weightValue < -5 || weightValue > 5) {
      console.error('Invalid signal weight value:', weight);
      return;
    }
    
    const signalWeights = { ...(user.curationSignature?.signalWeights || {}) };
    
    if (weightValue === 0) {
      delete signalWeights[signal];
    } else {
      signalWeights[signal] = weightValue;
    }
    
    let preferredTags = [...(user.curationSignature?.preferredTags || [])];
    let avoidedTags = [...(user.curationSignature?.avoidedTags || [])];
    
    if (weightValue > 0) {
      if (!preferredTags.includes(signal)) {
        preferredTags.push(signal);
      }
      avoidedTags = avoidedTags.filter(tag => tag !== signal);
    } else if (weightValue < 0) {
      if (!avoidedTags.includes(signal)) {
        avoidedTags.push(signal);
      }
      preferredTags = preferredTags.filter(tag => tag !== signal);
    } else {
      preferredTags = preferredTags.filter(tag => tag !== signal);
      avoidedTags = avoidedTags.filter(tag => tag !== signal);
    }
    
    const updatedUser = {
      ...user,
      curationSignature: {
        ...user.curationSignature,
        signalWeights,
        preferredTags,
        avoidedTags
      },
      history: {
        ...user.history,
        lastUpdated: Date.now()
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Add an influence
  const addInfluence = (influence) => {
    if (!user) return;
    
    const cleanInfluence = influence.trim();
    if (!cleanInfluence) return;
    
    const influences = [...(user.curationSignature?.influences || [])];
    if (influences.includes(cleanInfluence)) return;
    
    influences.push(cleanInfluence);
    
    const signalWeights = { ...(user.curationSignature?.signalWeights || {}) };
    signalWeights[cleanInfluence] = 5;
    
    const updatedUser = {
      ...user,
      curationSignature: {
        ...user.curationSignature,
        influences,
        signalWeights
      },
      history: {
        ...user.history,
        lastUpdated: Date.now()
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Remove an influence
  const removeInfluence = (influence) => {
    if (!user) return;
    
    const influences = (user.curationSignature?.influences || [])
      .filter(item => item !== influence);
    
    const signalWeights = { ...(user.curationSignature?.signalWeights || {}) };
    delete signalWeights[influence];
    
    const updatedUser = {
      ...user,
      curationSignature: {
        ...user.curationSignature,
        influences,
        signalWeights
      },
      history: {
        ...user.history,
        lastUpdated: Date.now()
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Update filter threshold
  const updateScoreThreshold = (threshold) => {
    if (!user) return;
    
    const thresholdValue = parseFloat(threshold);
    if (isNaN(thresholdValue) || thresholdValue < 0 || thresholdValue > 1) {
      console.error('Invalid threshold value:', threshold);
      return;
    }
    
    const updatedUser = {
      ...user,
      curationSignature: {
        ...user.curationSignature,
        scoreThreshold: thresholdValue
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Update filter mode (global, curated, personal-only)
  const updateFilterMode = (mode) => {
    if (!user) return;
    
    const validModes = ['global', 'curated', 'personal-only'];
    if (!validModes.includes(mode)) {
      console.error('Invalid filter mode:', mode);
      return;
    }
    
    const updatedUser = {
      ...user,
      settings: {
        ...user.settings,
        filterMode: mode
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Save an item
  const saveItem = (itemId) => {
    if (!itemId) return;
    
    const userId = getUserId();
    if (!userId) return;
    
    if (savedItems.includes(itemId)) {
      const updatedSavedItems = savedItems.filter(id => id !== itemId);
      setSavedItems(updatedSavedItems);
      localStorage.setItem(`artifact_saved_${userId}`, JSON.stringify(updatedSavedItems));
      
      if (user) {
        const likedItemIds = (user.history?.likedItemIds || [])
          .filter(id => id !== itemId);
        
        const updatedUser = {
          ...user,
          history: {
            ...user.history,
            likedItemIds,
            lastUpdated: Date.now()
          }
        };
        
        setUser(updatedUser);
        setTimeout(() => persistUserData(), 0);
      }
    } else {
      const updatedSavedItems = [...savedItems, itemId];
      setSavedItems(updatedSavedItems);
      localStorage.setItem(`artifact_saved_${userId}`, JSON.stringify(updatedSavedItems));
      
      if (dismissedItems.includes(itemId)) {
        const updatedDismissedItems = dismissedItems.filter(id => id !== itemId);
        setDismissedItems(updatedDismissedItems);
        localStorage.setItem(`artifact_dismissed_${userId}`, JSON.stringify(updatedDismissedItems));
      }
      
      if (user) {
        const likedItemIds = [...(user.history?.likedItemIds || [])];
        if (!likedItemIds.includes(itemId)) {
          likedItemIds.push(itemId);
        }
        
        const dismissedItemIds = (user.history?.dismissedItemIds || [])
          .filter(id => id !== itemId);
        
        const updatedUser = {
          ...user,
          history: {
            ...user.history,
            likedItemIds,
            dismissedItemIds,
            lastUpdated: Date.now()
          }
        };
        
        setUser(updatedUser);
        setTimeout(() => persistUserData(), 0);
      }
    }
  };
  
  // Dismiss an item
  const dismissItem = (itemId) => {
    if (!itemId) return;
    
    const userId = getUserId();
    if (!userId) return;
    
    const updatedDismissedItems = [...dismissedItems, itemId];
    setDismissedItems(updatedDismissedItems);
    localStorage.setItem(`artifact_dismissed_${userId}`, JSON.stringify(updatedDismissedItems));
    
    if (savedItems.includes(itemId)) {
      const updatedSavedItems = savedItems.filter(id => id !== itemId);
      setSavedItems(updatedSavedItems);
      localStorage.setItem(`artifact_saved_${userId}`, JSON.stringify(updatedSavedItems));
    }
    
    if (user) {
      const dismissedItemIds = [...(user.history?.dismissedItemIds || [])];
      if (!dismissedItemIds.includes(itemId)) {
        dismissedItemIds.push(itemId);
      }
      
      const likedItemIds = (user.history?.likedItemIds || [])
        .filter(id => id !== itemId);
      
      const updatedUser = {
        ...user,
        history: {
          ...user.history,
          dismissedItemIds,
          likedItemIds,
          lastUpdated: Date.now()
        }
      };
      
      setUser(updatedUser);
      setTimeout(() => persistUserData(), 0);
    }
  };
  
  // Check if an item is saved
  const isSaved = (itemId) => {
    return savedItems.includes(itemId);
  };
  
  // Check if an item is dismissed
  const isDismissed = (itemId) => {
    return dismissedItems.includes(itemId);
  };
  
  // Update Digital Card based on user interaction with content
  const updateDigitalCardFromInteraction = (item, interactionType) => {
    if (!user || !item) return;
    
    const itemSignals = Array.isArray(item.signals) 
      ? item.signals.map(signal => signal.toLowerCase()) 
      : [];
      
    const itemTags = Array.isArray(item.tags) 
      ? item.tags.map(tag => tag.toLowerCase()) 
      : [];
    
    const allSignals = [...new Set([...itemSignals, ...itemTags])];
    
    if (allSignals.length === 0) return;
    
    const signalWeights = { ...(user.curationSignature?.signalWeights || {}) };
    
    let updateStrength;
    switch (interactionType) {
      case 'like':
        updateStrength = 1;
        break;
      case 'dismiss':
        updateStrength = -1;
        break;
      case 'view':
        updateStrength = 0.2;
        break;
      default:
        updateStrength = 0;
    }
    
    if (updateStrength === 0) return;
    
    const maxTagsToUpdate = interactionType === 'like' ? 5 : 3;
    
    const prioritizedTags = [];
    
    allSignals.forEach(tag => {
      if (signalWeights[tag] !== undefined && prioritizedTags.length < maxTagsToUpdate) {
        prioritizedTags.push(tag);
      }
    });
    
    if (prioritizedTags.length < maxTagsToUpdate) {
      allSignals.forEach(tag => {
        if (signalWeights[tag] === undefined && prioritizedTags.length < maxTagsToUpdate) {
          prioritizedTags.push(tag);
        }
      });
    }
    
    prioritizedTags.forEach(tag => {
      if (!tag) return;
      
      const currentWeight = signalWeights[tag] || 0;
      
      let adjustment;
      if (interactionType === 'like') {
        adjustment = Math.max(0.5, 1 - (currentWeight / 5));
      } else if (interactionType === 'dismiss') {
        adjustment = Math.max(0.5, (currentWeight + 5) / 10);
      } else {
        adjustment = 0.5;
      }
      
      adjustment *= updateStrength;
      
      let newWeight = currentWeight + adjustment;
      
      newWeight = Math.round(newWeight * 2) / 2;
      
      newWeight = Math.max(-5, Math.min(5, newWeight));
      
      if (newWeight !== currentWeight) {
        if (newWeight === 0) {
          delete signalWeights[tag];
        } else {
          signalWeights[tag] = newWeight;
        }
      }
    });
    
    const preferredTags = [];
    const avoidedTags = [];
    
    Object.entries(signalWeights).forEach(([signal, weight]) => {
      if (weight > 0 && !preferredTags.includes(signal)) {
        preferredTags.push(signal);
      } else if (weight < 0 && !avoidedTags.includes(signal)) {
        avoidedTags.push(signal);
      }
    });
    
    const updatedUser = {
      ...user,
      curationSignature: {
        ...user.curationSignature,
        signalWeights,
        preferredTags,
        avoidedTags
      },
      history: {
        ...user.history,
        lastUpdated: Date.now()
      }
    };
    
    setUser(updatedUser);
    setTimeout(() => persistUserData(), 0);
  };
  
  // Context value
  const contextValue = {
    user,
    firebaseUser,
    isLoading,
    error,
    savedItems,
    dismissedItems,
    saveItem,
    dismissItem,
    updateUser,
    updateUserPreferences,
    updateSignalWeight,
    addInfluence,
    removeInfluence,
    updateScoreThreshold,
    updateFilterMode,
    updateDigitalCardFromInteraction,
    isSaved,
    isDismissed
  };
  
  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;